import classNames from "classnames";
import { range } from "lodash";
import { forwardRef, useEffect, useRef } from "react";
import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { FlatTreeNode } from "~/components/build/Show/routes/list/-components/useTreeNodes";

// Vertical guide line positions — centered on the parent icon at each depth.
// depth 0 icon center: pl-1.5 (0.375rem) + arrow w-7 (1.75rem) + half icon w-6 (0.75rem) = 2.875rem
// depth 1 icon center: pl-1.5 + arrow w-7 + spacer 1.75rem + half icon = 4.625rem
// Subtract 0.5px to center the 1px border-left on the icon center.
const GUIDE_LINE_LEFT: Record<number, string> = {
  1: "calc(2.875rem - 0.5px)",
  2: "calc(4.625rem - 0.5px)",
};

// Horizontal connector endpoint — start of icon column at each depth.
// depth 1: pl-1.5 + arrow w-7 + spacer 1.75rem = 3.875rem
// depth 2: pl-1.5 + arrow w-7 + spacer 3.5rem = 5.625rem
const CONNECTOR_RIGHT: Record<number, string> = {
  1: "3.875rem",
  2: "5.625rem",
};

interface TreeItemProps extends React.HTMLAttributes<HTMLDivElement> {
  node: FlatTreeNode;
  depth: number;
  children: React.ReactNode;
  isLastChild: boolean;
  isLastSticky?: boolean;
  /**
   * When set, this row is part of the pinned sticky stack. The item
   * registers itself in `stickyElsRef` under this index so
   * useStickyHeaders can find it without a DOM query, and clears any
   * imperative push offset on the row when it leaves the stack.
   */
  stickyIndex?: number;
  stickyElsRef?: React.MutableRefObject<Map<number, HTMLElement>>;
}

/**
 * Tree item wrapper with ARIA treeitem attributes and visual guide
 * lines / connectors for the step tree hierarchy.
 */
export const TreeItem = forwardRef<HTMLDivElement, TreeItemProps>(
  (
    { node, depth, isLastChild, isLastSticky, stickyIndex, stickyElsRef, children, ...rest },
    ref,
  ) => {
    const isExpanded =
      node.collapsibleState === CollapsibleState.Expanded ||
      node.collapsibleState === CollapsibleState.Full;

    // Local ref shadows the forwarded one so the sticky-registration effect
    // can read the element. The combined ref below keeps both in sync.
    const localRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (stickyIndex == null || !stickyElsRef) {
        return;
      }
      const element = localRef.current;
      if (!element) {
        return;
      }
      stickyElsRef.current.set(stickyIndex, element);
      return () => {
        // Clear any imperative push offset on the way out — otherwise if
        // this row enters the stack again before the next scroll fires,
        // it would render with the previous push's offset baked in.
        element.style.removeProperty("--sticky-push-offset");
        stickyElsRef.current.delete(stickyIndex);
      };
    }, [stickyIndex, stickyElsRef]);

    const setRef = (element: HTMLDivElement | null) => {
      localRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    return (
      <div
        ref={setRef}
        role="treeitem"
        aria-level={depth < 0 ? 0 : depth}
        aria-expanded={node.collapsibleState !== CollapsibleState.None ? isExpanded : undefined}
        aria-posinset={node.index + 1}
        aria-setsize={undefined}
        aria-hidden={node.ariaHidden || undefined}
        tabIndex={-1}
        className={classNames("group relative flex w-full items-baseline bg-white", {
          "*:opacity-50": node.deemphasized,
        })}
        {...rest}
      >
        {children}

        {isLastSticky && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 -bottom-[2px] z-30 h-[2px] bg-gradient-to-b from-black/[0.08] via-black/[0.02] to-transparent [mask-image:linear-gradient(to_right,transparent,black_80px,black_calc(100%-80px),transparent)] dark:from-white/[0.08] dark:via-white/[0.02]"
          />
        )}

        {/* Vertical guide lines for ancestor depths — skip when the ancestor is the last child */}
        {depth > 0 &&
          range(1, depth).map((level) => {
            const ancestor = node.parentPath[level];
            if (ancestor?.isLastChild) {
              return null;
            }
            return (
              <div
                key={`v-${level}`}
                className="not-inverted pointer-events-none absolute bottom-0 top-0 z-10 border-l border-gray-400 peer-aria-[current=step]:bottom-[2px] peer-aria-[current=step]:top-[2px] dark:border-gray-900"
                style={{ left: GUIDE_LINE_LEFT[level] }}
              />
            );
          })}

        {/* Current depth connector — vertical line + horizontal branch */}
        {depth > 0 && (
          <>
            {/* Vertical line: full height for non-last, top-half only for last child */}
            <div
              className={classNames(
                "not-inverted pointer-events-none absolute top-0 z-10 border-l border-gray-400 peer-aria-[current=step]:top-[2px] dark:border-gray-900",
                {
                  "bottom-0 peer-aria-[current=step]:bottom-[2px]": !isLastChild,
                },
              )}
              style={{
                left: GUIDE_LINE_LEFT[depth],
                ...(isLastChild ? { height: "50%" } : {}),
              }}
            />
            {/* Horizontal branch from guide line to icon */}
            <div
              className="not-inverted pointer-events-none absolute z-10 border-b border-gray-400 dark:border-gray-900"
              style={{
                left: GUIDE_LINE_LEFT[depth],
                width: `calc(${CONNECTOR_RIGHT[depth]} - ${GUIDE_LINE_LEFT[depth]})`,
                top: "50%",
              }}
            />
          </>
        )}
      </div>
    );
  },
);
